


































































































































































































































































































































































































































































import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import {
    DxDataGrid,
    DxColumn,
    DxEditing,
    DxSelection,
    DxScrolling,
} from 'devextreme-vue/data-grid';
import DataSource from 'devextreme/data/data_source';
import {
    CONTRACTS,
    FetchServiceContract,
    WindowsServiceContract,
    EventsServiceContract,
    Events,
} from '@/core/contracts';
import { encodeParams, lookupField, Inject } from '@/core/helpers';
import {
    Consumption,
    StructuredResult,
    ConsumptionsType,
    ConsumptionEditData,
} from '@/dto';
import {
    getModifiedItems,
    columnTemplates,
} from '@/core/components/table';
import { Table } from '@/core/components/table/types';
import {
    ContextsState,
    ProductsState,
    UserState,
} from '@/store';
import { defaultTableConfig, TableConfig } from '@/core/components/table';
import { localize } from '@/core/filters';
import { duration } from '@/core/filters/duration';
import { ConsumptionsPageArgs, ConsumptionPerformancePageArgs, ConsumptionReasonPageArgs } from './types';
import { MicroeconomicsPageArgs } from '../microeconomics';
import { DateTime } from 'luxon';

@Component({
    components: {
        DxDataGrid,
        DxColumn,
        DxSelection,
        DxEditing,
        DxScrolling,
    },
})
export default class ConsumptionsTable extends Vue {
    @Prop() public args: ConsumptionsPageArgs | undefined;
    @Prop(String) public tableKey!: string;
    @Prop(String) public exportFileName!: string;
    @Prop(Boolean) public colorIndication!: boolean;
    public dataSource = new DataSource({
        key: 'id' as keyof Consumption,
        load: this.load,
        update: () => Promise.resolve()
    });

    @Inject(CONTRACTS.FetchService)
    private _fetchService?: FetchServiceContract;
    @Inject(CONTRACTS.WindowsService)
    private _windowsService?: WindowsServiceContract;
    @Inject(CONTRACTS.EventsService)
    private _eventsService?: EventsServiceContract;

    private usageResourceTypes: any[] = [];

    public get currentProduct() {
        return ProductsState.current;
    }

    public get defaultTableConfig(): Partial<TableConfig> {
        return defaultTableConfig(this);
    }

    public get ConsumptionsType() {
        return ConsumptionsType;
    }

    public get columnTemplates() {
        return columnTemplates;
    }

    public get userIsAdministrator() {
        return UserState.current && UserState.current.isDev;
    }

    public get changeDeliveryProposalComments() {
        const comments = localize('ChangeDeliveryProposalComments');
        return Array.isArray(comments) ? comments : [];
    }

    public async mounted() {
        this._eventsService!.on(
            Events.ProcessingFinished,
            this.onProcessingFinished
        );
    }

    public async beforeDestroy() {
        this._eventsService!.off(
            Events.ProcessingFinished,
            this.onProcessingFinished
        );
    }

    public getDataGridInstance(): Table {
        const element = this.$refs['data-grid'] as unknown;
        return (element as DxDataGrid).instance!;
    }

    public onRowUpdated(args: { key: any }) {
        this.getDataGridInstance().selectRows([args.key], true);
    }

    public showAvailableStocks() {
        this._windowsService!.showDialog('Not implemented!');
    }

    public openConsumptionPerformancePage(packAlias: string) {
        this._windowsService!.open('consumption-performance-page', {
            contextId: ContextsState.current!.id,
            packAlias,
        } as ConsumptionPerformancePageArgs);
    }
    public openConsumptionReasonPage(packAlias: string) {
        this._windowsService!.open('consumption-reason-page', {
            contextId: ContextsState.current!.id,
            packAlias,
        } as ConsumptionReasonPageArgs);
    }

    public getSelectedItems() {
        return getModifiedItems<Consumption>(this.getDataGridInstance());
    }

    public toggleImportantDemand(item: Consumption) {
        this.setImportantDemandFlag([item], !item.important);
    }

    public showRoutes(data: any) {
        if (data.data.routeSourcesCount > 1) {
            this._windowsService!.update('microeconomics-page', {
                packAlias: data.data.packAlias,
                direction: 'In',
            } as MicroeconomicsPageArgs);
        } else {
            this._windowsService!.update('microeconomics-details-page', {
                packAlias: data.data.packAlias,
                direction: 'In',
                routeIndex: 0,
            } as MicroeconomicsPageArgs);
        }
    }

    public setImportantDemandFlag(items: Consumption[], important: boolean) {
        const data = items.map(item => ({
            packId: item.packId,
            important,
        }));

        const currentContextId = ContextsState.currentContextId;
        this._fetchService!.post(
            `/contexts/${currentContextId}/edit-consumptions`,
            data
        ).then(() => {
            items.forEach(item => (item.important = important));
        });
    }

    public processEdit(
        internalTime: any,
        internalQuantity: any,
        usedResourceTypes: number[]
    ) {
        const items = this.getSelectedItems();
        if (items.length > 0) {
            const currentContextId = ContextsState.currentContextId;
            const data = this.createEditData(
                items,
                internalTime,
                internalQuantity,
                usedResourceTypes
            );
            return this._fetchService!.post(
                `/contexts/${currentContextId}/edit-consumptions`,
                data
            ).then(() => {
                this.getDataGridInstance().clearSelection();
            });
        }
        return Promise.resolve();
    }

    private onProcessingFinished = ({ contextId }: any) => {
        if (Number(ContextsState.currentContextId) === Number(contextId)) {
            this.getDataGridInstance().refresh();
        }
    };

    private async load() {
        const currentContextId = ContextsState.currentContextId;
        const url = `/contexts/${currentContextId}/consumptions?${encodeParams(
            this.makeFilter()
        )}`;
        return this._fetchService!.get<StructuredResult<Consumption>>(url).then(
            (data: StructuredResult<Consumption>) => {
                if (!data) {
                    return {
                        data: [],
                        totalCount: 0,
                    };
                }
                this.transform(data);

                const items = data.items || [];

                return {
                    data: items,
                    totalCount: items.length,
                };
            }
        );
    }

    private makeFilter() {
        const filter: any = {};
        if (this.currentProduct) {
            filter.productAlias = this.currentProduct.alias;
        }

        let queryType: any = this.args!.type;
        let queryAlias = filter.productAlias;
        let queryProject = null;
        let queryExtData = null;

        switch (this.args!.type) {
            case ConsumptionsType.STOCK: {
                queryType = null;
                queryAlias = this.args!.productAlias;
                queryProject = this.args!.sourceProject;
                break;
            }

            case ConsumptionsType.COMPENSATION: {
                queryType = 'all';
                queryAlias = this.args!.productAlias;
                queryProject = this.args!.sourceProject;
                break;
            }

            case ConsumptionsType.PRELIMINARY: {
                queryType = null;
                queryExtData = 'предварительные';
                break;
            }
        }

        if (queryType) {
            filter.type = queryType;
        }

        if (this.args!.extdata) {
            queryExtData = this.args!.extdata;
        }

        if (queryAlias) {
            filter.productAlias = queryAlias;
        }

        if (queryProject) {
            filter.projectName = queryProject;
        }

        if (queryExtData) {
            filter.extdata = queryExtData;
        }

        return filter;
    }

    private transform(data: StructuredResult<Consumption>) {
        if (!(data && data.items)) {
            return;
        }

        const usageResourceTypes = new Set();

        for (let index = 0; index < data.items.length; index++) {
            const item = data.items[index];
            item.id = index;

            // for excel print
            const anyPack = item as any;
            anyPack.packId_isActive = !!anyPack.packId;

            lookupField(item, 'product', data.products);
            lookupField(item, 'site', data.sites);
            lookupField(item, 'preferredSite', data.sites);
            lookupField(item, 'owner', data.owners);
            lookupField(item, 'project', data.projects);
            lookupField(item, 'object', data.objects);
            lookupField(item, 'stateType', data.strings);
            lookupField(item, 'markerType', data.strings);
            lookupField(item, 'orderNumber', data.strings);
            lookupField(item, 'externalData', data.strings);

            const sourceSitesId =
                item.sourceSitesId !== undefined ? item.sourceSitesId : [];
            const sourceSites = [];
            for (const i of sourceSitesId) {
                sourceSites.push(data.sites[i].name);
            }
            item.sourceSites = sourceSites.join(', ');

            item.productType = data.resourceTypes[item.product.typeId];
            item.volume = item.quantity / item.product.displayDenominator;
            item.satisfiedVolume =
                item.satisfied / item.product.displayDenominator;

            if (item.plannedIncomingQuantity) {
                item.plannedIncomingVolume =
                    item.plannedIncomingQuantity /
                    item.product.displayDenominator;
            }

            if (item.consignmentId) {
                item.consignment = data.strings[item.consignmentId];
            }

            if (item.internalQuantity) {
                item.internalQuantityVolume =
                    item.internalQuantity / item.product.displayDenominator;
            }

            if (!item.agreementDescription) {
                item.agreementDescription = '';
            }

            item.hasMaxDelay = item.maxDelay !== undefined;

            // Calculate agreementStatusName
            if (item.reviewDeadline) {
                item.agreementStatusName = localize(
                    'TitleButtonAgreementDelayed'
                );
            }

            // Deviation duration
            if (item.receiptTime && item.preferredTime) {
                const preferredTimeDate = new Date(item.preferredTime);
                const receiptTimeDate = new Date(item.receiptTime);
                item.deviationDuration = duration(receiptTimeDate, preferredTimeDate);
            }

            // Add exist usage resources types
            item.usageResourceTypeIds?.forEach(rt => {
                usageResourceTypes.add(data.resourceTypes[rt]);
            });
        }

        this.usageResourceTypes = Array.from(usageResourceTypes);
    }

    private createEditData(
        items: Consumption[],
        time: any,
        quantity: any,
        usedResourceTypes: number[]
    ): ConsumptionEditData[] {
        return items.map(
            item =>
                ({
                    packId: item.packId,
                    internalTime: time,
                    internalQuantity: quantity,
                    usedResourceTypes,
                } as ConsumptionEditData)
        );
    }

    private onRowPrepared(rowInfo: any) {
        if (!this.colorIndication) {
            return;
        }

        if (rowInfo.rowElement && rowInfo.rowType === 'data') {
            const rowClass: string | undefined = this.getRowClass(
                rowInfo.data.volume,
                rowInfo.data.satisfiedVolume,
                rowInfo.data.preferredTime,
                rowInfo.data.receiptTime,
                rowInfo.data.active
            );

            if (rowClass) {
                rowInfo.rowElement.addClass(rowClass);
            }
        }
    }

    private getRowClass(
        volume: number,
        satisfiedVolume: number,
        time: DateTime,
        receiptTime: DateTime,
        active: boolean
    ): string | undefined {
        // inactive
        if (active === false) {
            return 'text-disabled';
        }

        // not fully satisfied
        if (volume > 0 && satisfiedVolume < volume) {
            return 'text-error';
        }

        // postponed
        if (time < receiptTime) {
            return 'text-warning';
        }

        return undefined;
    }
}
